<script setup lang="ts">
import { ref } from 'vue'
import { healthService } from '@/api/health'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import {
  faSkull,
  faCalendarDay,
  faClipboardList,
  faMicroscope,
  faUserMd,
  faSave,
  faTimes,
  faChevronDown,
  faCheckCircle,
  faCircleExclamation,
  faCircleNotch,
  faExclamationTriangle,
  faNotesMedical
} from '@fortawesome/free-solid-svg-icons'

library.add(
  faSkull,
  faCalendarDay,
  faClipboardList,
  faMicroscope,
  faUserMd,
  faSave,
  faTimes,
  faChevronDown,
  faCheckCircle,
  faCircleExclamation,
  faCircleNotch,
  faExclamationTriangle,
  faNotesMedical
)

interface Animal {
  id: string
  species: string
}

const props = defineProps<{
  animal: Animal
}>()

const emit = defineEmits<{
  success: []
}>()

const loading = ref(false)
const errorMessage = ref('')
const successMessage = ref('')

const formData = ref({
  date_of_death: new Date().toISOString().split('T')[0],
  cause: 'Unknown',
  cause_description: '',
  suspected_cause: '',
  necropsy_performed: false,
  post_mortem_findings: '',
  necropsy_performed_by: ''
})

const causeOptions = [
  'Natural', 'Disease', 'Injury', 'Predation', 'Unknown', 'Euthanasia', 'Other'
]

async function submitForm() {
  loading.value = true
  errorMessage.value = ''
  successMessage.value = ''

  try {
    await healthService.createMortalityRecord({
      ...formData.value,
      animal: props.animal.id
    })
    
    successMessage.value = 'Mortality record finalized successfully.'
    setTimeout(() => {
      emit('success')
    }, 1500)
  } catch (err: any) {
    errorMessage.value = err.response?.data?.detail || err.message || 'Failed to record mortality'
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <form @submit.prevent="submitForm" class="space-y-6">
    <!-- Mandatory Warning Alert -->
    <div class="bg-rose-50 border-2 border-rose-100 rounded-3xl p-6 flex items-start gap-4">
      <div class="w-12 h-12 rounded-2xl bg-rose-100 flex items-center justify-center text-rose-600 shrink-0">
        <FontAwesomeIcon icon="exclamation_triangle" class="text-xl" />
      </div>
      <div>
        <h4 class="text-rose-800 font-black text-sm uppercase tracking-wider">Critical Action Required</h4>
        <p class="text-rose-600/80 text-sm font-bold leading-relaxed">
          Recording this mortality event will permanently mark 
          <span class="text-rose-700 underline underline-offset-4">Animal ID: {{ animal.id }}</span> 
          as deceased and remove it from active inventory.
        </p>
      </div>
    </div>

    <!-- Event Essentials Section -->
    <div class="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
      <div class="p-6 border-b border-slate-50 bg-slate-50/50">
        <h3 class="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
          <div class="w-1.5 h-6 bg-slate-900 rounded-full"></div>
          Mortality Incident Data
        </h3>
      </div>
      
      <div class="p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
        <!-- Date of Death -->
        <div class="space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Date of Loss <span class="text-rose-500">*</span></label>
          <div class="relative group">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-slate-900 transition-colors">
              <FontAwesomeIcon icon="calendar-day" />
            </div>
            <input 
              v-model="formData.date_of_death" 
              type="date" 
              required 
              class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10 text-slate-700 font-bold transition-all"
            >
          </div>
        </div>

        <!-- Cause of Death -->
        <div class="space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Primary Cause <span class="text-rose-500">*</span></label>
          <div class="relative group">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-slate-900 transition-colors">
              <FontAwesomeIcon icon="skull" />
            </div>
            <select 
              v-model="formData.cause" 
              required
              class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10 text-slate-700 font-bold transition-all appearance-none cursor-pointer"
            >
              <option v-for="cause in causeOptions" :key="cause" :value="cause">{{ cause }}</option>
            </select>
            <div class="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-slate-300">
              <FontAwesomeIcon icon="chevron-down" class="text-xs" />
            </div>
          </div>
        </div>

        <!-- Cause Description -->
        <div class="col-span-1 md:col-span-2 space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Incident Description</label>
          <div class="relative group">
            <div class="absolute top-4 left-4 pointer-events-none text-slate-300 group-focus-within:text-slate-900 transition-colors">
              <FontAwesomeIcon icon="clipboard-list" />
            </div>
            <textarea 
              v-model="formData.cause_description" 
              rows="3" 
              placeholder="Provide a detailed account of the events leading to the loss..." 
              class="block w-full pl-11 pr-4 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-slate-900 focus:ring-4 focus:ring-slate-900/10 text-slate-700 font-bold transition-all"
            ></textarea>
          </div>
        </div>

        <!-- Suspected Cause (Conditional) -->
        <div v-if="formData.cause === 'Unknown'" class="col-span-1 md:col-span-2 space-y-2">
          <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Presumptive Diagnostics</label>
          <div class="relative group">
            <div class="absolute top-4 left-4 pointer-events-none text-slate-300 group-focus-within:text-rose-500 transition-colors">
              <FontAwesomeIcon icon="notes-medical" />
            </div>
            <textarea 
              v-model="formData.suspected_cause" 
              rows="2" 
              placeholder="What are the suspected underlying factors?" 
              class="block w-full pl-11 pr-4 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-rose-500 focus:ring-4 focus:ring-rose-500/10 text-slate-700 font-bold transition-all"
            ></textarea>
          </div>
        </div>
      </div>
    </div>

    <!-- Clinical Examination Section -->
    <div class="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
      <div class="p-4 border-b border-slate-50 bg-slate-50/50 flex items-center justify-between">
        <h3 class="text-lg font-black text-slate-800 tracking-tight flex items-center gap-2">
          <div class="w-1.5 h-6 bg-blue-500 rounded-full"></div>
          Post-Mortem Examination
        </h3>
        <label class="relative inline-flex items-center cursor-pointer group">
          <input type="checkbox" v-model="formData.necropsy_performed" class="sr-only peer">
          <div class="w-14 h-8 bg-slate-100 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600 transition-all"></div>
          <span class="ml-3 text-xs font-black text-slate-500 uppercase tracking-widest group-hover:text-slate-700 transition-colors">Performed</span>
        </label>
      </div>

      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="transform -translate-y-4 opacity-0" enter-to-class="transform translate-y-0 opacity-100">
        <div v-if="formData.necropsy_performed" class="p-8 space-y-8">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div class="space-y-2">
              <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Lead Practitioner</label>
              <div class="relative group">
                <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-300 group-focus-within:text-blue-500 transition-colors">
                  <FontAwesomeIcon icon="user-md" />
                </div>
                <input 
                  v-model="formData.necropsy_performed_by" 
                  type="text" 
                  placeholder="Veterinarian or Pathologist"
                  class="block w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 text-slate-700 font-bold transition-all"
                >
              </div>
            </div>
            
            <div class="space-y-2">
              <!-- Empty space for layout balance -->
            </div>
          </div>

          <div class="space-y-2">
            <label class="block text-xs font-black text-slate-400 uppercase tracking-widest px-1">Pathology Findings</label>
            <div class="relative group">
              <div class="absolute top-4 left-4 pointer-events-none text-slate-300 group-focus-within:text-blue-500 transition-colors">
                <FontAwesomeIcon icon="microscope" />
              </div>
              <textarea 
                v-model="formData.post_mortem_findings" 
                rows="3" 
                placeholder="Document macroscopic and microscopic pathological findings..." 
                class="block w-full pl-11 pr-4 py-4 bg-slate-50 border-2 border-transparent rounded-2xl focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 text-slate-700 font-bold transition-all"
              ></textarea>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <!-- Alert Messages -->
    <div class="space-y-3">
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="transform -translate-y-4 opacity-0" enter-to-class="transform translate-y-0 opacity-100">
        <div v-if="successMessage" class="bg-emerald-50 border border-emerald-100 text-emerald-700 rounded-2xl p-4 flex items-center shadow-sm font-bold">
          <FontAwesomeIcon icon="check-circle" class="mr-3 text-lg" />
          {{ successMessage }}
        </div>
      </transition>
      
      <transition enter-active-class="transition duration-300 ease-out" enter-from-class="transform -translate-y-4 opacity-0" enter-to-class="transform translate-y-0 opacity-100">
        <div v-if="errorMessage" class="bg-rose-50 border border-rose-100 text-rose-700 rounded-2xl p-4 flex items-center shadow-sm font-bold">
          <FontAwesomeIcon icon="circle-exclamation" class="mr-3 text-lg" />
          {{ errorMessage }}
        </div>
      </transition>
    </div>

    <div class="flex justify-end gap-3 pt-4 border-t border-slate-100">
      <button 
        type="button" 
        @click="$emit('success')" 
        class="px-8 py-3.5 text-slate-400 font-black rounded-2xl hover:bg-slate-50 hover:text-slate-600 transition-all active:scale-95 flex items-center gap-2"
      >
        <FontAwesomeIcon icon="times" />
        Discard
      </button>
      <button 
        type="submit" 
        :disabled="loading"
        class="px-10 py-3.5 bg-rose-600 text-white font-black rounded-2xl hover:bg-rose-700 transition-all shadow-xl shadow-rose-100 active:scale-95 disabled:bg-slate-200 disabled:shadow-none flex items-center gap-3"
      >
        <FontAwesomeIcon v-if="loading" icon="circle-notch" class="animate-spin" />
        <FontAwesomeIcon v-else icon="save" />
        {{ loading ? 'Recording Incident...' : 'Finalize Death Record' }}
      </button>
    </div>
  </form>
</template>
